import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from './store';
import { hasAnyRole, type Role } from './types';

interface RoleGuardProps {
  /** User must hold at least one of these roles to access the children. */
  roles: Role[];
}

/**
 * RoleGuard — declarative RBAC for routes.
 *
 * Wraps a sub-tree of the router. Assumes ProtectedRoute has already
 * placed an authenticated user in the store (otherwise redirects to
 * /login first). When the user lacks every required role, redirects
 * to /403 with `replace` so the back button does not loop.
 *
 * Usage:
 *   {
 *     element: <RoleGuard roles={['fleet_manager', 'isi_admin']} />,
 *     children: [{ path: 'admin', element: <AdminPage /> }],
 *   }
 */
export function RoleGuard({ roles }: RoleGuardProps) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAnyRole(user, roles)) return <Navigate to="/403" replace />;
  return <Outlet />;
}
